import { cn } from 'cn';
import { type PropsWithChildren } from 'react';
import { Unit } from '~/atoms/Unit';

import {
  getFormattedNumberPresentation,
  type NumberDisplayProps,
} from './getFormattedNumberPresentation';

export type FormattedNumberProps = PropsWithChildren<NumberDisplayProps>;

/**
 * A component that renders a formatted number with a unit.
 *
 * @param className - The class name to apply to the component.
 * @param classNameValue - The class name to apply to the value.
 * @param currency - The currency to use for formatting the number. Convenience function that renders the localized currency symbol as unit.
 * @param digits - The number of digits to display after the decimal point.
 * @param fallbackValue - The value to return if the number is undefined. Default is '—'.
 * @param forceSign - Whether to force the sign (+ or -) of the number to be displayed. Default is false.
 * @param highContrast - Whether to use high contrast mode. Default is false.
 * @param locale - The locale to use for formatting the number. Defaults to the active UI language.
 * @param minimumDigits - Digits always shown after the decimal point, e.g. to give a column of numbers one precision. Defaults to `digits`, at most 2.
 * @param style - The style to apply to the component.
 * @param unit - The unit to display after the number.
 * @param value - The value to format.
 * @param children - The children to render inside the component.
 * @returns a React node with the formatted number and unit.
 */
export const FormattedNumber = ({
  children,
  className,
  classNameValue,
  currency,
  digits = 'auto',
  fallbackValue = '—',
  forceSign = false,
  highContrast = false,
  locale,
  minimumDigits,
  style,
  unit,
  value,
}: FormattedNumberProps) => {
  const { formattedValue, hasNoValue, unitToRender } =
    getFormattedNumberPresentation({
      currency,
      digits,
      fallbackValue,
      forceSign,
      locale,
      minimumDigits,
      unit,
      value,
    });

  return (
    <span className={cn('inline-block', className)} style={style}>
      {children ? (
        children
      ) : (
        <span
          className={cn(
            {
              'text-tiga-text-lighter': hasNoValue,
            },
            classNameValue,
          )}
        >
          {formattedValue}
        </span>
      )}
      {Boolean(unitToRender) && (
        <Unit className="text-sm" highContrast={highContrast}>
          {unitToRender}
        </Unit>
      )}
    </span>
  );
};
